import {
	ChartSecuritiesTct,
	DealExtractRes,
	GetCapDetailsRes,
	GetCapTableDataDto,
	GetCapTableDataRes,
	GetCapTransactionsValidationDetailsRes,
	GetGroupStructureDataRes,
	GetGroupStructurePositionsDataRes,
	UpdateGroupStructurePositionsDto,
} from '@dealstack/shared/lib/generated';
import {
	fetchFileRequest,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';

// Get Cap Table Data
export const getCapTableData = async (endpoint: string, args: { body: GetCapTableDataDto }) => {
	const { body } = args;
	return makeRequest<ApiV1.Response<GetCapTableDataRes>>({
		endpoint: formatUnicorn(endpoint || API_PATHS.CAP_DATA_POST, {}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

// Get Group Structure Data
export const getCapChartData = async (body: GetCapTableDataDto) => {
	return makeRequest<ApiV1.Response<GetGroupStructureDataRes>>({
		endpoint: formatUnicorn(API_PATHS.CAP_GROUP_STRUCTURE_DATA_POST, {}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const getCapChartSecurities = async (body: Omit<GetCapTableDataDto, 'securityIds'>) => {
	return makeRequest<ApiV1.Response<ChartSecuritiesTct[]>>({
		endpoint: formatUnicorn(API_PATHS.CAP_GROUP_STRUCTURE_DATA_SECURITIES_POST, {}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

export const downloadCapExcelQuery = async (args: {
	format: string;
	filterDate?: string;
	// CAP-1999: the rail the table is being viewed on, so the export matches what is on screen.
	// Required, not optional: formatUnicorn substitutes by key, so a missing one would leave a
	// literal "{rail}" in the query string and the DTO's IsIn would reject it.
	rail: string;
}) => {
	const endpoint = formatUnicorn(
		`${API_PATHS.CAP_DOWNLOAD_EXCEL_GET}?format={format}&filterDate={filterDate}&rail={rail}`,
		args
	);
	const res = await fetchFileRequest(endpoint, {});
	if (!res) {
		throw new Error('Failed to download file');
	}
	return res.blob();
};

export const downloadCapHoldingsJsonQuery = async (args: {
	dealId: string;
	filterDate?: string;
	// CAP-1976: required, like the Excel export's. The extract used to take the legal rail while the
	// screen it was taken from defaults to beneficial, so the two disagreed silently.
	rail: string;
}) => {
	const endpoint = formatUnicorn(API_PATHS.CAP_DEAL_DEALID_EXPORT_FILE_GET, {
		dealId: args.dealId,
	});
	const searchParams = new URLSearchParams();

	if (args.filterDate) {
		searchParams.set('filterDate', args.filterDate);
	}
	searchParams.set('rail', args.rail);

	const endpointWithQuery =
		searchParams.size > 0 ? `${endpoint}?${searchParams.toString()}` : endpoint;

	return makeRequest<ApiV1.Response<DealExtractRes>>({
		endpoint: endpointWithQuery,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

// Get Cap Details
export const getCapDetailsForDashboard = async (endpoint: string) => {
	return makeRequest<ApiV1.Response<GetCapDetailsRes>>({
		endpoint,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getCapTransactionsValidatationDetails = async (endpoint: string) => {
	return makeRequest<ApiV1.Response<GetCapTransactionsValidationDetailsRes>>({
		endpoint,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

// Update Group Structure Positions
export const updateCapChartPositions = async (body: UpdateGroupStructurePositionsDto) => {
	return makeRequest<ApiV1.Response<GetGroupStructurePositionsDataRes>>({
		endpoint: API_PATHS.CAP_GROUP_STRUCTURE_POSITIONS_PUT,
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	}).then((response) => response.payload);
};

// Get Group Structure Positions
export const getGroupStructurePositions = async (endpoint: string) => {
	return makeRequest<ApiV1.Response<GetGroupStructurePositionsDataRes>>({
		endpoint: formatUnicorn(endpoint || API_PATHS.CAP_GROUP_STRUCTURE_POSITIONS_POST, {}),
		method: HTTP_METHOD.POST,
	}).then((response) => response.payload);
};
